import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { decodeShare } from 'wobbli/formats';
import { Header, ShareProvider, TabBar } from './components/Layout';
import { ToastProvider } from './components/ui';
import { baseSettings, SettingsProvider, useSettings } from './lib/settings';
import { StudioProvider, useEngine } from './studio/context';
import Home from './pages/Home';

const Studio = lazy(() => import('./pages/Studio'));
const States = lazy(() => import('./pages/States'));
const About = lazy(() => import('./pages/About'));
const Recipes = lazy(() => import('./pages/Recipes'));
const NotFound = lazy(() => import('./pages/NotFound'));

const TITLES: Record<string, string> = { studio: 'Studio', states: 'All states', recipes: 'Recipes', about: 'About' };
const LEGACY: Record<string, string> = { '#home': '/', '#studio': '/studio', '#states': '/states', '#about': '/about' };

/** Share links (#studio~...) and the prototype's hash routes (#studio) open the right page. */
function Hashes() {
  const loc = useLocation();
  const nav = useNavigate();
  const { replace } = useSettings();
  const engine = useEngine();
  useEffect(() => {
    let h = loc.hash;
    try { h = decodeURIComponent(h); } catch { /* keep it as is */ }
    if (h.startsWith('#studio~')) {
      const d = decodeShare(h, baseSettings());
      if (d) {
        replace(d.settings);
        engine.pickCos = null;
        if (d.live) engine.restore(d.live.state, d.live.variant);
      }
      nav('/studio', { replace: true });
    } else if (LEGACY[h]) nav(LEGACY[h], { replace: true });
  }, [loc.hash]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!loc.hash) window.scrollTo(0, 0); }, [loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const route = loc.pathname === '/' ? 'home' : loc.pathname.slice(1);
    document.documentElement.dataset.route = route;
    document.title = TITLES[route] ? TITLES[route] + ' · Wobbli' : route === 'home' ? 'Wobbli' : 'Not found · Wobbli';
  }, [loc.pathname]);
  return null;
}

export default function App() {
  return (
    <SettingsProvider>
      <StudioProvider>
        <ToastProvider>
          <ShareProvider>
            <Hashes />
            <a className="skip" href="#main">Skip to content</a>
            <Header />
            <TabBar />
            <main id="main">
              <Suspense fallback={<div className="wrap page-loading" aria-busy="true" />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/studio" element={<Studio />} />
                  <Route path="/states" element={<States />} />
                  <Route path="/recipes" element={<Recipes />} />
                  <Route path="/about" element={<About />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </main>
          </ShareProvider>
        </ToastProvider>
      </StudioProvider>
    </SettingsProvider>
  );
}

